import {
  Box,
  Alert,
  AlertIcon,
  AlertDescription,
  Skeleton,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useGetOrganizationImplementationsQuery } from '../../requests/implementations.gql.generated';
import { AddImplementationButton } from './AddImplementationButton';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { TableBETA } from '~/packages/common-frontend/tables/TableBETA';

type TableData = {
  id: string;
  name: string;
  creditFacilities: { id: string; name: string }[];
};

const columnHelper = createColumnHelper<TableData>();
const columnDefs = [
  columnHelper.accessor('id', {
    header: 'Id',
    cell: ({ getValue }) => getValue(),
    enableSorting: false,
  }),
  columnHelper.accessor('name', {
    header: 'Name',
    cell: ({ getValue }) => getValue(),
  }),
  columnHelper.accessor('creditFacilities', {
    header: 'Credit Facilities',
    cell: ({ getValue }) =>
      getValue()
        .map((cf) => cf.name)
        .join(', '),
    enableSorting: false,
  }),
];

export const ImplementationsList = ({
  organizationId,
  implementationRoutePrefix,
}: {
  organizationId: string;
  implementationRoutePrefix: string; // e.g. `/organizations/{orgId}/implementations/`
}) => {
  const navigate = useNavigate();

  const { data, error, loading } = useGetOrganizationImplementationsQuery({
    variables: { organizationId },
  });

  const implementations: TableData[] | undefined = useMemo(() => {
    return data?.organization?.implementations ?? undefined;
  }, [data]);

  const table = useReactTable({
    data: implementations ?? [],
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertDescription>Failed to load implementations</AlertDescription>
      </Alert>
    );
  }

  if (loading || !implementations) return <Skeleton />;

  return (
    <Box margin="2rem 0">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb="1rem"
      >
        <Typography variant="section-title">Implementations</Typography>
        <AddImplementationButton ownerOrganizationId={organizationId} />
      </Box>
      <Box mb="1.5rem">
        <Typography>
          This organization is the{' '}
          <Typography inline fontWeight="bold">
            owner
          </Typography>{' '}
          of these implementations. The owner need not be the borrower.
          Ownership should be assigned such that in a self-serve world, we would
          allow the implementation owner to edit any credit agreement in the
          implementation. If an implementation is assigned an incorrect owner,
          contact engineers.
        </Typography>
      </Box>
      <TableBETA
        table={table}
        onRowClick={(row) =>
          navigate(`${implementationRoutePrefix}${row.original.id}`)
        }
      />
      {implementations.length === 0 && (
        <Alert>
          <AlertIcon />
          <AlertDescription>No implementations configured</AlertDescription>
        </Alert>
      )}
    </Box>
  );
};
